import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";
import StopIcon from "@mui/icons-material/Stop";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useTranslation } from "react-i18next";
import { FlespiVehicleCameraData } from "../../../../models/vehicle/flespiDataTypes";
import {
    FlespiVideoStreamPlayer,
    FlespiVideoStreamPlayerHandle,
    openLiveStream,
    closeLiveStream,
} from "../../../flespiVideoStreamPlayer/flespiVideoStreamPlayer";

interface CameraChannel {
    deviceFlespiId: string;
    deviceName: string;
    channelIdx: number;
    channelName: string;
}

interface CameraStreamDialogProps {
    open: boolean;
    onClose: () => void;
    vehicleRegisterNumber: string;
    cameraData: FlespiVehicleCameraData;
}

const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 480;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 280;

const CameraStreamDialog = ({
    open,
    onClose,
    vehicleRegisterNumber,
    cameraData,
}: CameraStreamDialogProps) => {
    const { t } = useTranslation();
    const playerRef = useRef<FlespiVideoStreamPlayerHandle>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(false);
    const [streaming, setStreaming] = useState(false);
    const streamingRef = useRef(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedChannelIndex, setSelectedChannelIndex] = useState(0);

    // Position & size state
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [size, setSize] = useState({
        width: DEFAULT_WIDTH,
        height: DEFAULT_HEIGHT,
    });
    const [initialized, setInitialized] = useState(false);

    // Drag state
    const dragRef = useRef<{
        dragging: boolean;
        startX: number;
        startY: number;
        startPosX: number;
        startPosY: number;
    } | null>(null);

    // Resize state
    const resizeRef = useRef<{
        resizing: boolean;
        startX: number;
        startY: number;
        startWidth: number;
        startHeight: number;
    } | null>(null);

    // Center on first open
    useEffect(() => {
        if (open && !initialized) {
            setPosition({
                x: Math.max(0, (window.innerWidth - DEFAULT_WIDTH) / 2),
                y: Math.max(0, (window.innerHeight - DEFAULT_HEIGHT) / 2),
            });
            setInitialized(true);
        }
    }, [open, initialized]);

    // Reset initialized when closed so next open re-centers
    useEffect(() => {
        if (!open) {
            setInitialized(false);
        }
    }, [open]);

    // Drag handlers
    const handleDragStart = useCallback(
        (e: React.MouseEvent) => {
            // Only drag from the title bar area
            if ((e.target as HTMLElement).closest("button, select, input"))
                return;
            e.preventDefault();
            dragRef.current = {
                dragging: true,
                startX: e.clientX,
                startY: e.clientY,
                startPosX: position.x,
                startPosY: position.y,
            };
        },
        [position]
    );

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (dragRef.current?.dragging) {
                const dx = e.clientX - dragRef.current.startX;
                const dy = e.clientY - dragRef.current.startY;
                setPosition({
                    x: dragRef.current.startPosX + dx,
                    y: dragRef.current.startPosY + dy,
                });
            }
            if (resizeRef.current?.resizing) {
                const dx = e.clientX - resizeRef.current.startX;
                const dy = e.clientY - resizeRef.current.startY;
                setSize({
                    width: Math.max(
                        MIN_WIDTH,
                        resizeRef.current.startWidth + dx
                    ),
                    height: Math.max(
                        MIN_HEIGHT,
                        resizeRef.current.startHeight + dy
                    ),
                });
            }
        };
        const handleMouseUp = () => {
            if (dragRef.current) dragRef.current.dragging = false;
            if (resizeRef.current) resizeRef.current.resizing = false;
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    // Resize handle
    const handleResizeStart = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            resizeRef.current = {
                resizing: true,
                startX: e.clientX,
                startY: e.clientY,
                startWidth: size.width,
                startHeight: size.height,
            };
        },
        [size]
    );

    const channels = useMemo<CameraChannel[]>(() => {
        const result: CameraChannel[] = [];
        for (const device of cameraData?.cameraDevices ?? []) {
            if (device.error) continue;
            for (const cam of device.configuration?.cameras ?? []) {
                result.push({
                    deviceFlespiId: device.id.toString(),
                    deviceName: device.name,
                    channelIdx: cam.channel ?? 0,
                    channelName: cam.name ?? `Channel ${cam.channel ?? 0}`,
                });
            }
        }
        return result;
    }, [cameraData]);

    const selectedChannel = channels[selectedChannelIndex] ?? null;

    const setStreamingState = useCallback((value: boolean) => {
        streamingRef.current = value;
        setStreaming(value);
    }, []);

    const stopCurrentStream = useCallback(() => {
        if (streamingRef.current && selectedChannel && playerRef.current) {
            closeLiveStream(
                playerRef,
                selectedChannel.deviceFlespiId,
                selectedChannel.channelIdx
            );
            setStreamingState(false);
        }
    }, [selectedChannel, setStreamingState]);

    const startStream = useCallback(() => {
        if (!selectedChannel || !playerRef.current) return;
        setError(null);
        openLiveStream(
            playerRef,
            selectedChannel.deviceFlespiId,
            selectedChannel.channelIdx
        );
        setStreamingState(true);
    }, [selectedChannel, setStreamingState]);

    // Track whether a channel switch should auto-start a stream
    const pendingChannelSwitch = useRef(false);

    // Auto-start stream when dialog opens or after channel switch
    // Uses streamingRef to avoid re-triggering when user manually stops
    useEffect(() => {
        if (!open || !selectedChannel) return;
        if (streamingRef.current && !pendingChannelSwitch.current) return;
        pendingChannelSwitch.current = false;
        // Delay to let the FlespiVideoStreamPlayer mount and expose its ref
        const timer = setTimeout(() => {
            if (playerRef.current) {
                startStream();
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [open, selectedChannel, startStream]);

    // Clean up stream on unmount
    useEffect(() => {
        return () => {
            stopCurrentStream();
        };
    }, [stopCurrentStream]);

    const handleClose = () => {
        stopCurrentStream();
        setError(null);
        setLoading(false);
        onClose();
    };

    const handleChannelChange = (newIndex: number) => {
        stopCurrentStream();
        pendingChannelSwitch.current = true;
        setSelectedChannelIndex(newIndex);
    };

    const handleStreamError = () => {
        setStreamingState(false);
        setError(t("monitorView.camera.streamError"));
    };

    const handleStreamException = () => {
        setStreamingState(false);
        setError(t("monitorView.camera.streamError"));
    };

    if (!cameraData || channels.length === 0) return null;
    if (!open) return null;

    return createPortal(
        <Paper
            ref={panelRef}
            elevation={8}
            onClick={(e) => e.stopPropagation()}
            sx={{
                position: "fixed",
                left: position.x,
                top: position.y,
                width: size.width,
                height: size.height,
                zIndex: 1300,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}
        >
            {/* Draggable title bar */}
            <Box
                onMouseDown={handleDragStart}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    cursor: "move",
                    backgroundColor: "primary.dark",
                    color: "primary.contrastText",
                    userSelect: "none",
                    flexShrink: 0,
                }}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <VideocamIcon fontSize="small" />
                    <Typography variant="subtitle1" noWrap>
                        {vehicleRegisterNumber} -{" "}
                        {t("monitorView.camera.liveStream")}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={handleClose}
                    sx={{ color: "primary.contrastText" }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            {/* Content area */}
            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    p: 1.5,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Channel selector when multiple cameras */}
                {channels.length > 1 && (
                    <Box sx={{ mb: 1, flexShrink: 0 }}>
                        <Select
                            size="small"
                            value={selectedChannelIndex}
                            onChange={(e) =>
                                handleChannelChange(e.target.value as number)
                            }
                            fullWidth
                        >
                            {channels.map((ch, idx) => (
                                <MenuItem
                                    key={`${ch.deviceFlespiId}-${ch.channelIdx}`}
                                    value={idx}
                                >
                                    {ch.deviceName} - {ch.channelName}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                )}

                {/* Video player */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {loading && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            sx={{ py: 2 }}
                        >
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Typography color="error" sx={{ py: 1 }}>
                            {error}
                        </Typography>
                    )}
                    <Box sx={{ width: "100%", flex: 1, minHeight: 0 }}>
                        <FlespiVideoStreamPlayer
                            ref={playerRef}
                            onLoadingChange={setLoading}
                            onStreamOperationError={handleStreamError}
                            onStreamOperationException={handleStreamException}
                        />
                    </Box>
                </Box>

                {/* Controls */}
                <Box
                    display="flex"
                    justifyContent="center"
                    gap={2}
                    sx={{ mt: 1, flexShrink: 0 }}
                >
                    {streaming ? (
                        <IconButton
                            color="error"
                            onClick={stopCurrentStream}
                            size="large"
                        >
                            <StopIcon fontSize="large" />
                        </IconButton>
                    ) : (
                        <IconButton
                            color="primary"
                            onClick={startStream}
                            size="large"
                            disabled={loading}
                        >
                            <PlayArrowIcon fontSize="large" />
                        </IconButton>
                    )}
                </Box>
            </Box>

            {/* Resize handle (bottom-right corner) */}
            <Box
                onMouseDown={handleResizeStart}
                sx={{
                    position: "absolute",
                    right: 0,
                    bottom: 0,
                    width: 16,
                    height: 16,
                    cursor: "nwse-resize",
                    "&::after": {
                        content: '""',
                        position: "absolute",
                        right: 3,
                        bottom: 3,
                        width: 0,
                        height: 0,
                        borderStyle: "solid",
                        borderWidth: "0 0 10px 10px",
                        borderColor: "transparent transparent #bbb transparent",
                    },
                }}
            />
        </Paper>,
        document.body
    );
};

export default CameraStreamDialog;
